import React from "react";
import styled, { css } from "styled-components";
import { cloud } from "@vitalsource/vstui/colors";
import { MountOnce } from "../../../components/mixins";

const cardStyles = css`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  overflow: hidden;
`;

const Styled = {
  CardLi: styled.li`
    ${cardStyles};
  `,
  CardDiv: styled.div`
    ${cardStyles};
  `,
  CoverArea: styled.div`
    position: relative;
    overflow: hidden;
    // Important: Grow the cover area vertically if needed to fill space!
    flex: 1;
  `,
  Footer: styled.div`
    padding: 16px;
    border-top: 1px solid ${cloud.getShade(3)};
    font-weight: 500;
    font-size: 1.4rem;
    line-height: 1.9rem;
  `,
  BlurryImage: styled.img`
    width: 200%;
    height: 200%;
    position: absolute;
    display: block;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  RealImage: styled.img`
    object-fit: contain;
    width: 100%;
    height: 100%;
    position: relative;
  `
};

/**
 * Basic stretchable book card, for use with CSS Grid
 *
 * Our prior cards are all fixed aspect ratio. This is instead a base card with a flexible aspect ratio
 * and the ability to stretch. That is:
 *   1. The cover image area is given a preferred aspect ratio but will grow vertically if needed to
 *      fill the row (relevant if combined with other cards with different layouts)
 *   2. The (external) footer should be set up to be font size aware, which will allow it to grow
 *      vertically if the user changes the font size
 *   3. The overall card aspect ratio is thus determined by the preferred cover aspect ratio, the
 *      font-aware footer height, and any image stretch required to fill the row
 *
 * @see FlexibleBookGrid
 */
const FlexibleBookCard = ({
  book,
  width, // from swimlane
  skeleton, // from swimlane
  preferredCoverAspectRatio,
  remBasedFooterContents,
  endContent
}) => {
  // Fixed width for swimlane, dynamic for grid
  const isFixedSize = !!width;

  // `as` prop only works in styled-components 4+
  // swimlane wraps card with li, grid doesn't and cards don't stretch right with an extra layer of wrapping
  const OuterCard = isFixedSize ? Styled.CardDiv : Styled.CardLi;

  return (
    <OuterCard style={isFixedSize ? { width } : undefined}>
      <Styled.CoverArea style={{ aspectRatio: preferredCoverAspectRatio }}>
        <MountOnce reveal={!skeleton}>
          <Styled.BlurryImage
            aria-hidden
            src={book.coverURL.replace(":width", "20")}
            alt=""
          />
        </MountOnce>
        <MountOnce reveal={!skeleton}>
          <Styled.RealImage
            alt=""
            src={book.coverURL.replace(":width", "320")}
            srcSet={`${book.coverURL.replace(":width", "640")} 2x`}
          />
        </MountOnce>
      </Styled.CoverArea>
      <Styled.Footer>{remBasedFooterContents}</Styled.Footer>
      {endContent}
    </OuterCard>
  );
};

export default FlexibleBookCard;
